import React, { useState, useRef } from 'react';
import './App.css';
import Webcam from 'react-webcam';
import { 
  Camera, Upload, Brain, Play, Loader, Apple, Beef, 
  Salad, Coffee, Cake, Heart, AlertTriangle, ChefHat,
  Leaf, Flame, Activity, TrendingUp, Shield
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// Simple i18n strings (default: pt-BR)
const STRINGS = {
  pt: {
    tagline: 'Nutrição com IA',
    instruction: 'Tire foto ou carregue imagem do alimento',
    instruction_tail: 'para análise nutricional, calorias, alergênos e receitas saudáveis!',
    use_camera: 'Usar Câmera',
    hide_camera: 'Ocultar Câmera',
    upload_image: 'Carregar Imagem',
    try_random: 'Testar Exemplo',
    loading_sample: 'Carregando...',
    settings: 'Configurações',
    settings_personalized: 'Configurações Personalizadas',
    flip_camera: 'Inverter Câmera',
    capture: 'Capturar',
    no_image: 'Nenhuma imagem selecionada',
    placeholder_hint: 'Envie uma imagem ou use sua câmera',
    personal_settings: 'Configurações Pessoais',
    allergen_alerts: 'Alertas de Alergênos',
    dietary_prefs: 'Preferências Alimentares',
    health_goal: 'Objetivo de Saúde',
    trans_fat_toggle: 'Avise se houver gordura trans',
    analyze: 'Analisar Nutrição',
    analyzing: 'IA Analisando... Aguarde 20-25s ⏳',
    generate_recipe: 'Gerar Receita',
    generate_personalized_recipe: 'Gere uma Receita Personalizada',
    generate_another_recipe: 'Gerar Outra Receita',
    generating: 'IA Gerando Receita... Aguarde 2-3s ⏳',
    allergen_alert_title: '⚠️ ALERTA DE ALERGÊNOS!',
    this_food_contains: 'Este alimento contém:',
    trans_fat_alert_title: '⚠️ ALERTA DE GORDURA TRANS!',
    trans_fat_alert_text: 'Este alimento pode conter gordura trans (óleos hidrogenados).',
    total_nutrition: 'Nutrição Total',
    calories: 'Calorias',
    protein: 'Proteína',
    carbs: 'Carboidratos',
    fat: 'Gorduras',
    health_score: 'Índice de Saúde',
    detected_items: 'Itens Alimentares Detectados',
    allergens_label: 'Alergênos:',
    ingredients_label: 'Ingredientes:',
    contains_trans_fat: '⚠️ Contém gorduras trans - não recomendado',
    recipe_suggestion: 'Sugestão de Receita Saudável',
    footer_line1: 'eaTFit • Análise Nutricional com IA',
    footer_line2: 'Coma bem, fique em forma! 💪🥗',
    lang_pt: 'Português (BR)',
    lang_es: 'Español',
    lang_en: 'English',
    goal_balanced: 'Dieta Balanceada',
    goal_weight_loss: 'Emagrecimento',
    goal_muscle_gain: 'Ganho de Massa',
    goal_heart_healthy: 'Saúde do Coração',
    goal_keto: 'Keto',
    goal_vegan: 'Vegana',
    modes: {
      general: { name: 'Todos os Alimentos', desc: 'Analise qualquer tipo de alimento' },
      fruits: { name: 'Apenas Frutas', desc: 'Foco em frutas e seus benefícios' },
      vegetables: { name: 'Vegetais', desc: 'Analise vegetais e folhas' },
      meat: { name: 'Proteínas/Carnes', desc: 'Foco em fontes de proteína' },
      mixed: { name: 'Refeições Completas', desc: 'Analise pratos com múltiplos itens' },
      desserts: { name: 'Sobremesas', desc: 'Analise doces e sobremesas' },
      beverages: { name: 'Bebidas', desc: 'Analise sucos, refrigerantes, cafés etc.' }
    },
    allergens: {
      'Milk': 'Leite',
      'Eggs': 'Ovos',
      'Fish': 'Peixe',
      'Shellfish': 'Frutos do mar',
      'Tree nuts': 'Nozes',
      'Peanuts': 'Amendoim',
      'Wheat': 'Trigo',
      'Gluten': 'Glúten',
      'Soybeans': 'Soja',
      'Sesame': 'Gergelim'
    },
    diet_prefs: {
      'Vegetarian': 'Vegetariano',
      'Vegan': 'Vegano',
      'Gluten Free': 'Sem glúten',
      'Dairy Free': 'Sem lactose',
      'Keto': 'Keto',
      'Paleo': 'Paleo',
      'Low Carb': 'Baixo carbo',
      'Low Sodium': 'Baixo sódio'
    },
    compat: {
      vegetarian: 'Vegetariano',
      vegan: 'Vegano',
      gluten_free: 'Sem glúten',
      dairy_free: 'Sem lactose',
      keto_friendly: 'Keto',
      low_carb: 'Baixo carbo'
    }
  },
  es: {
    tagline: 'Nutrición con IA',
    instruction: 'Tome foto o cargue imagen del alimento',
    instruction_tail: 'para análisis nutricional, calorías, alérgenos y recetas saludables!',
    use_camera: 'Usar Cámara',
    hide_camera: 'Ocultar Cámara',
    upload_image: 'Subir Imagen',
    try_random: 'Probar Muestra',
    loading_sample: 'Cargando...',
    settings: 'Ajustes',
    settings_personalized: 'Ajustes Personalizados',
    flip_camera: 'Cambiar Cámara',
    capture: 'Capturar',
    no_image: 'No hay imagen seleccionada',
    placeholder_hint: 'Sube una imagen o usa tu cámara',
    personal_settings: 'Ajustes Personales',
    allergen_alerts: 'Alertas de Alérgenos',
    dietary_prefs: 'Preferencias Dietéticas',
    health_goal: 'Objetivo de Salud',
    trans_fat_toggle: 'Avisarme si hay grasa trans',
    analyze: 'Analizar Nutrición',
    analyzing: 'IA Analizando... Espere 20-25s ⏳',
    generate_recipe: 'Generar Receta',
    generate_personalized_recipe: 'Genere una Receta Personalizada',
    generate_another_recipe: 'Generar Otra Receta',
    generating: 'IA Generando Receta... Espere 2-3s ⏳',
    allergen_alert_title: '⚠️ ¡ALERTA DE ALÉRGENOS!',
    this_food_contains: 'Este alimento contiene:',
    trans_fat_alert_title: '⚠️ ¡ALERTA DE GRASA TRANS!',
    trans_fat_alert_text: 'Este alimento puede contener grasa trans (aceites hidrogenados).',
    total_nutrition: 'Nutrición Total',
    calories: 'Calorías',
    protein: 'Proteína',
    carbs: 'Carbohidratos',
    fat: 'Grasas',
    health_score: 'Índice de Salud',
    detected_items: 'Ítems de Comida Detectados',
    allergens_label: 'Alérgenos:',
    ingredients_label: 'Ingredientes:',
    contains_trans_fat: '⚠️ Contiene grasas trans - no recomendado',
    recipe_suggestion: 'Sugerencia de Receta Saludable',
    footer_line1: 'eaTFit • Análisis Nutricional con IA',
    footer_line2: '¡Come Inteligente, Mantente en Forma! 💪🥗',
    lang_pt: 'Português (BR)',
    lang_es: 'Español',
    lang_en: 'English',
    goal_balanced: 'Dieta Equilibrada',
    goal_weight_loss: 'Pérdida de Peso',
    goal_muscle_gain: 'Ganancia Muscular',
    goal_heart_healthy: 'Salud del Corazón',
    goal_keto: 'Keto',
    goal_vegan: 'Vegana',
    modes: {
      general: { name: 'Todos los Alimentos', desc: 'Analiza cualquier tipo de alimento' },
      fruits: { name: 'Solo Frutas', desc: 'Enfoque en frutas y sus beneficios' },
      vegetables: { name: 'Vegetales', desc: 'Analiza vegetales y hojas' },
      meat: { name: 'Proteínas/Carnes', desc: 'Enfoque en fuentes de proteína' },
      mixed: { name: 'Comidas Completas', desc: 'Analiza platos con múltiples ítems' },
      desserts: { name: 'Postres', desc: 'Analiza dulces y postres' },
      beverages: { name: 'Bebidas', desc: 'Analiza jugos, refrescos, cafés, etc.' }
    },
    allergens: {
      'Milk': 'Leche',
      'Eggs': 'Huevos',
      'Fish': 'Pescado',
      'Shellfish': 'Mariscos',
      'Tree nuts': 'Frutos secos',
      'Peanuts': 'Maní',
      'Wheat': 'Trigo',
      'Gluten': 'Gluten',
      'Soybeans': 'Soja',
      'Sesame': 'Sésamo'
    },
    diet_prefs: {
      'Vegetarian': 'Vegetariano',
      'Vegan': 'Vegano',
      'Gluten Free': 'Sin gluten',
      'Dairy Free': 'Sin lácteos',
      'Keto': 'Keto',
      'Paleo': 'Paleo',
      'Low Carb': 'Bajo en carbohidratos',
      'Low Sodium': 'Bajo en sodio'
    },
    compat: {
      vegetarian: 'Vegetariano',
      vegan: 'Vegano',
      gluten_free: 'Sin gluten',
      dairy_free: 'Sin lácteos',
      keto_friendly: 'Apto para Keto',
      low_carb: 'Bajo en carbohidratos'
    }
  },
  en: {
    tagline: 'AI Nutrition',
    instruction: 'Take photo or upload food image',
    instruction_tail: 'for nutrition analysis, calories, allergens and healthy recipes!',
    use_camera: 'Use Camera',
    hide_camera: 'Hide Camera',
    upload_image: 'Upload Image',
    try_random: 'Try Sample',
    loading_sample: 'Loading...',
    settings: 'Settings',
    settings_personalized: 'Personalized Settings',
    flip_camera: 'Flip Camera',
    capture: 'Capture',
    no_image: 'No image selected',
    placeholder_hint: 'Upload an image or use your camera',
    personal_settings: 'Personal Settings',
    allergen_alerts: 'Allergen Alerts',
    dietary_prefs: 'Dietary Preferences',
    health_goal: 'Health Goal',
    trans_fat_toggle: 'Alert me if the food contains trans fat',
    analyze: 'Analyze Nutrition',
    analyzing: 'AI Analyzing... Wait 20-25s ⏳',
    generate_recipe: 'Generate Recipe',
    generate_personalized_recipe: 'Generate Personalized Recipe',
    generate_another_recipe: 'Generate Another Recipe',
    generating: 'AI Generating Recipe... Wait 2-3s ⏳',
    allergen_alert_title: '⚠️ ALLERGEN ALERT!',
    this_food_contains: 'This food contains:',
    trans_fat_alert_title: '⚠️ TRANS FAT ALERT!',
    trans_fat_alert_text: 'This food may contain trans fat (hydrogenated oils/shortening).',
    total_nutrition: 'Total Nutrition',
    calories: 'Calories',
    protein: 'Protein',
    carbs: 'Carbs',
    fat: 'Fat',
    health_score: 'Health Score',
    detected_items: 'Detected Food Items',
    allergens_label: 'Allergens:',
    ingredients_label: 'Ingredients:',
    contains_trans_fat: '⚠️ Contains trans fats - not recommended',
    recipe_suggestion: 'Healthy Recipe Suggestion',
    footer_line1: 'eaTFit • AI-Powered Smart Nutrition Analysis',
    footer_line2: 'Eat Smart, Stay Fit! 💪🥗',
    lang_pt: 'Português (BR)',
    lang_es: 'Español',
    lang_en: 'English',
    goal_balanced: 'Balanced Diet',
    goal_weight_loss: 'Weight Loss',
    goal_muscle_gain: 'Muscle Gain',
    goal_heart_healthy: 'Heart Healthy',
    goal_keto: 'Keto',
    goal_vegan: 'Vegan',
    modes: {
      general: { name: 'All Foods', desc: 'Analyze any type of food' },
      fruits: { name: 'Fruits Only', desc: 'Focus on fruits and their benefits' },
      vegetables: { name: 'Vegetables', desc: 'Analyze vegetables and greens' },
      meat: { name: 'Protein/Meat', desc: 'Focus on meat and protein sources' },
      mixed: { name: 'Complete Meals', desc: 'Analyze full meals with multiple items' },
      desserts: { name: 'Desserts', desc: 'Analyze sweets and desserts' },
      beverages: { name: 'Beverages', desc: 'Analyze drinks and beverages' }
    },
    allergens: {
      'Milk': 'Milk', 'Eggs': 'Eggs', 'Fish': 'Fish', 'Shellfish': 'Shellfish', 'Tree nuts': 'Tree nuts',
      'Peanuts': 'Peanuts', 'Wheat': 'Wheat', 'Gluten': 'Gluten', 'Soybeans': 'Soybeans', 'Sesame': 'Sesame'
    },
    diet_prefs: {
      'Vegetarian': 'Vegetarian', 'Vegan': 'Vegan', 'Gluten Free': 'Gluten Free', 'Dairy Free': 'Dairy Free',
      'Keto': 'Keto', 'Paleo': 'Paleo', 'Low Carb': 'Low Carb', 'Low Sodium': 'Low Sodium'
    },
    compat: {
      vegetarian: 'Vegetarian',
      vegan: 'Vegan',
      gluten_free: 'Gluten free',
      dairy_free: 'Dairy free',
      keto_friendly: 'Keto friendly',
      low_carb: 'Low carb'
    }
  }
};

const FOOD_MODES = {
  general: { name: 'All Foods', icon: Apple, color: '#10b981', description: 'Analyze any type of food' },
  fruits: { name: 'Fruits Only', icon: Apple, color: '#f59e0b', description: 'Focus on fruits and their benefits' },
  vegetables: { name: 'Vegetables', icon: Leaf, color: '#22c55e', description: 'Analyze vegetables and greens' },
  meat: { name: 'Protein/Meat', icon: Beef, color: '#ef4444', description: 'Focus on meat and protein sources' },
  mixed: { name: 'Complete Meals', icon: Salad, color: '#8b5cf6', description: 'Analyze full meals with multiple items' },
  desserts: { name: 'Desserts', icon: Cake, color: '#ec4899', description: 'Analyze sweets and desserts' },
  beverages: { name: 'Beverages', icon: Coffee, color: '#06b6d4', description: 'Analyze drinks and beverages' }
};

const ALLERGEN_OPTIONS = [
  'Milk', 'Eggs', 'Fish', 'Shellfish', 'Tree nuts', 
  'Peanuts', 'Wheat', 'Gluten', 'Soybeans', 'Sesame'
];

const DIETARY_PREFERENCES = [
  'Vegetarian', 'Vegan', 'Gluten Free', 'Dairy Free', 
  'Keto', 'Paleo', 'Low Carb', 'Low Sodium'
];

function App() {
  const [mode, setMode] = useState('general');
  const [image, setImage] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [recipe, setRecipe] = useState(null);
  const [recipeText, setRecipeText] = useState(null);
  const [recipeImage, setRecipeImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSample, setLoadingSample] = useState(false);
  const [useWebcam, setUseWebcam] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [showSettings, setShowSettings] = useState(false);
  const [lang, setLang] = useState('pt'); // 'pt' | 'es' | 'en'
  const [samplePreloaded, setSamplePreloaded] = useState(false);
  
  // User preferences
  const [userAllergens, setUserAllergens] = useState([]);
  const [dietaryPreferences, setDietaryPreferences] = useState([]);
  const [healthGoal, setHealthGoal] = useState('balanced');
  const [transFatAlert, setTransFatAlert] = useState(false);
  
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const isFirstLoad = useRef(true);
  const recipeRef = useRef(null);

  // Pre-load a sample image on first load
  React.useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      loadRandomSample();
    }
  }, []);

  const handleModeChange = (newMode) => {
    setMode(newMode);
    setAnalysis(null);
    setRecipe(null);
  };

  const captureImage = () => {
    const imageSrc = webcamRef.current.getScreenshot();
    setImage(imageSrc);
    setUseWebcam(false);
    setAnalysis(null);
    setRecipe(null);
    setRecipeText(null);
    setRecipeImage(null);
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
        setAnalysis(null);
        setRecipe(null);
        setRecipeText(null);
        setRecipeImage(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const analyzeFood = async () => {
    if (!image) return;
    
    setLoading(true);
    setAnalysis(null);
    
    try {
      const formData = new FormData();
      
      // Convert base64 to blob
      const response = await fetch(image);
      const blob = await response.blob();
      formData.append('image', blob, 'food.jpg');
      formData.append('mode', mode);
      formData.append('user_allergens', JSON.stringify(userAllergens));
      formData.append('dietary_preferences', JSON.stringify(dietaryPreferences));
      formData.append('language', lang);
      
      const result = await axios.post(`${API_URL}/api/analyze-food`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (result.data.success) {
        setAnalysis(result.data);
      }
    } catch (error) {
      console.error('Error analyzing food:', error);
      alert('Error analyzing food: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const generateRecipe = async () => {
    if (!analysis) return;
    
    setLoading(true);
    setRecipe(null); // Clear previous recipe text
    setRecipeText(null); // Clear temporary recipe text
    setRecipeImage(null); // Clear previous image
    
    try {
      const result = await axios.post(`${API_URL}/api/generate-recipe`, {
        food_items: analysis.analysis.food_items,
        health_goal: healthGoal,
        language: lang
      });
      
      if (result.data.success) {
        setRecipeText(result.data.recipe); // Store recipe text temporarily
        
        // Use the ORIGINAL analyzed food items to match image (not the recipe query)
        const originalFoodItems = analysis.analysis.food_items.map(item => item.name.toLowerCase()).join(' ');
        console.log('Original analyzed food:', originalFoodItems);
        
        // Curated food image library - ORDER MATTERS (most specific first)
        const imageMap = [
          // Smoothies and beverages - MUST be first to avoid salad fallback
          { keywords: ['smoothie', 'shake', 'batido', 'vitamina'], urls: [
            'https://images.unsplash.com/photo-1505252585461-04db1eb84625?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1623065422902-30a2d299bbe4?w=400&h=300&fit=crop'
          ]},
          { keywords: ['juice', 'suco', 'jugo'], urls: ['https://images.unsplash.com/photo-1600271886742-f049cd451bba?w=400&h=300&fit=crop'] },
          // Burgers - multiple options
          { keywords: ['burger', 'hamburger', 'hamburguer', 'hambúrguer'], urls: [
            'https://images.unsplash.com/photo-1568901346375-23c9450c58cd?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1550547660-d9450f859349?w=400&h=300&fit=crop',
            'https://images.unsplash.com/photo-1572802419224-296b0aeee0d9?w=400&h=300&fit=crop'
          ]},
          { keywords: ['lasagna', 'lasanha'], urls: ['https://images.unsplash.com/photo-1574894709920-11b28e7367e3?w=400&h=300&fit=crop'] },
          { keywords: ['pizza'], urls: ['https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400&h=300&fit=crop'] },
          { keywords: ['salmon', 'salmão'], urls: ['https://images.unsplash.com/photo-1485921325833-c519f76c4927?w=400&h=300&fit=crop'] },
          { keywords: ['steak', 'bife'], urls: ['https://images.unsplash.com/photo-1600891964092-4316c288032e?w=400&h=300&fit=crop'] },
          { keywords: ['chicken', 'frango'], urls: ['https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=400&h=300&fit=crop'] },
          { keywords: ['beef', 'carne'], urls: ['https://images.unsplash.com/photo-1588168333986-5078d3ae3976?w=400&h=300&fit=crop'] },
          { keywords: ['pasta', 'macarrão', 'spaghetti'], urls: ['https://images.unsplash.com/photo-1621996346565-e3dbc646d9a9?w=400&h=300&fit=crop'] },
          { keywords: ['fish', 'peixe'], urls: ['https://images.unsplash.com/photo-1559847844-5315695dadae?w=400&h=300&fit=crop'] },
          { keywords: ['soup', 'sopa'], urls: ['https://images.unsplash.com/photo-1547592166-23ac45744acd?w=400&h=300&fit=crop'] },
          { keywords: ['rice', 'arroz'], urls: ['https://images.unsplash.com/photo-1516684732162-798a0062be99?w=400&h=300&fit=crop'] },
          { keywords: ['fruit', 'fruta'], urls: ['https://images.unsplash.com/photo-1490474418585-ba9bad8fd0ea?w=400&h=300&fit=crop'] },
          { keywords: ['dessert', 'sobremesa', 'cake', 'bolo'], urls: ['https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?w=400&h=300&fit=crop'] },
          { keywords: ['sandwich', 'sanduíche'], urls: ['https://images.unsplash.com/photo-1528735602780-2552fd46c7af?w=400&h=300&fit=crop'] },
          // Salad at the end to avoid unwanted matches
          { keywords: ['salad', 'salada'], urls: ['https://images.unsplash.com/photo-1546069901-ba9599a7e63c?w=400&h=300&fit=crop'] }
        ];
        
        // Find best matching image based on ORIGINAL food
        let selectedImage = null;
        let matchedKeyword = null;
        
        // Try to match keywords from original food
        for (const item of imageMap) {
          for (const keyword of item.keywords) {
            if (originalFoodItems.includes(keyword)) {
              // Select random image from available URLs
              const urls = item.urls;
              selectedImage = urls[Math.floor(Math.random() * urls.length)];
              matchedKeyword = keyword;
              break;
            }
          }
          if (selectedImage) break;
        }
        
        // Fallback to general healthy food image
        if (!selectedImage) {
          selectedImage = 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?w=400&h=300&fit=crop';
          matchedKeyword = 'fallback';
        }
        
        // Add timestamp for cache busting
        const randomSeed = Date.now();
        selectedImage += `&t=${randomSeed}`;
        
        console.log(`Matched keyword '${matchedKeyword}' for original food '${originalFoodItems}'`);
        console.log('Selected recipe image:', selectedImage);
        setRecipeImage(selectedImage);
      }
    } catch (error) {
      console.error('Error generating recipe:', error);
      alert('Error generating recipe: ' + (error.response?.data?.error || error.message));
      setLoading(false);
    }
    // Note: setLoading(false) is now handled in image onLoad/onError
  };

  const loadRandomSample = async () => {
    if (loadingSample) return; // Prevent double-clicks
    
    // Clear previous results first (immediate visual feedback)
    setAnalysis(null);
    setRecipe(null);
    setRecipeText(null);
    setRecipeImage(null);
    setLoadingSample(true);

    const loadFromPaths = async (paths, retries = 3) => {
      for (const path of paths) {
        for (let attempt = 0; attempt <= retries; attempt++) {
          try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout
            
            const resp = await fetch(path, { 
              cache: 'force-cache',
              signal: controller.signal
            });
            clearTimeout(timeoutId);
            
            if (!resp.ok) continue;
            const blob = await resp.blob();
            const reader = new FileReader();
            const dataUrl = await new Promise((resolve, reject) => {
              reader.onloadend = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(blob);
            });
            setImage(dataUrl);
            setSamplePreloaded(true);
            return true;
          } catch (err) {
            console.log(`Attempt ${attempt + 1} failed for ${path}:`, err.message);
            if (attempt === retries) {
              // Last attempt for this path, try next path
              break;
            }
            // Wait 1 second before retry
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      return false;
    };

    try {
      const result = await axios.get(`${API_URL}/api/random-sample`, { timeout: 30000 });
      if (result.data?.success && result.data.sample) {
        const s = result.data.sample;
        const clean = s.url?.startsWith('/') ? s.url : `/${s.url || ''}`;
        const candidates = [
          `${API_URL}${clean}`,
          clean
        ];
        if (await loadFromPaths(candidates)) {
          return;
        }
      }
      // Fallback: local list under frontend/public/samples
      const local = [
        'sample_salmon.jpg',
        'sample_salad.jpg',
        'sample_burger.jpg',
        'sample_pasta.jpg',
        'sample_fruits.jpg'
      ];
      const pick = local[Math.floor(Math.random() * local.length)];
      const candidates = [
        `/samples/${pick}`,
        `${API_URL}/samples/${pick}`
      ];
      if (await loadFromPaths(candidates)) {
        return;
      }
      throw new Error('All sample paths failed');
    } catch (error) {
      console.error('Could not load a sample image:', error);
      // Silently fail - don't show alert, just log to console
      // User can try again by clicking the button
    } finally {
      setLoadingSample(false);
    }
  };

  const toggleAllergen = (allergen) => {
    setUserAllergens(prev =>
      prev.includes(allergen)
        ? prev.filter(a => a !== allergen)
        : [...prev, allergen]
    );
  };

  const toggleDietaryPref = (pref) => {
    setDietaryPreferences(prev =>
      prev.includes(pref)
        ? prev.filter(p => p !== pref)
        : [...prev, pref]
    );
  };

  const t = STRINGS[lang] || STRINGS.pt;
  const ModeIcon = FOOD_MODES[mode]?.icon || Apple;
  const SHOW_MODE_SELECTOR = false;

  return (
    <div className="App">
      <header className="App-header">
        <div className="header-content">
          <Activity size={40} className="logo" />
          <h1>eaTFit</h1>
          <p>{t.tagline}</p>
        </div>
        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '8px', justifyContent: 'center' }}>
          <button
            onClick={() => setLang('pt')}
            title={t.lang_pt}
            aria-label={t.lang_pt}
            className={`control-btn lang-flag ${lang === 'pt' ? 'active' : ''}`}
            style={{ padding: '6px 10px' }}
          >
            🇧🇷
          </button>
          <button
            onClick={() => setLang('es')}
            title={t.lang_es}
            aria-label={t.lang_es}
            className={`control-btn lang-flag ${lang === 'es' ? 'active' : ''}`}
            style={{ padding: '6px 10px' }}
          >
            🇪🇸
          </button>
          <button
            onClick={() => setLang('en')}
            title={t.lang_en}
            aria-label={t.lang_en}
            className={`control-btn lang-flag ${lang === 'en' ? 'active' : ''}`}
            style={{ padding: '6px 10px' }}
          >
            🇬🇧
          </button>
        </div>
      </header>

      {/* Instruction Banner */}
      <div className="instruction-banner">
        <span className="banner-icon">💡</span>
        <p>
          <strong>{t.instruction}</strong> {t.instruction_tail}
        </p>
      </div>

      <div className="container">
        {/* Food Mode Selector (hidden for now) */}
        {SHOW_MODE_SELECTOR && (
          <div className="mode-selector">
            {Object.entries(FOOD_MODES).map(([key, config]) => {
              const Icon = config.icon;
              const name = (STRINGS[lang]?.modes?.[key]?.name) || config.name;
              const desc = (STRINGS[lang]?.modes?.[key]?.desc) || config.description;
              return (
                <button 
                  key={key}
                  className={`mode-btn ${mode === key ? 'active' : ''}`}
                  onClick={() => handleModeChange(key)}
                  style={{ '--mode-color': config.color }}
                  title={desc}
                >
                  <Icon size={20} />
                  {name}
                </button>
              );
            })}
          </div>
        )}

        <div className="content-container">
          {/* Image Section */}
          <div className="image-section">
            <div className="image-controls">
              <button onClick={() => {
                setUseWebcam(!useWebcam);
                if (!useWebcam) {
                  // Clear previous analysis and recipe when opening camera
                  setAnalysis(null);
                  setRecipe(null);
                  setRecipeText(null);
                  setRecipeImage(null);
                }
              }} className="control-btn">
                <Camera size={18} />
                {useWebcam ? t.hide_camera : t.use_camera}
              </button>
              <button onClick={() => fileInputRef.current?.click()} className="control-btn">
                <Upload size={18} />
                {t.upload_image}
              </button>
              <button 
                onClick={loadRandomSample} 
                disabled={loadingSample || loading}
                className="control-btn secondary"
                style={{ 
                  color: loadingSample ? '#1e3a8a' : undefined
                }}
              >
                <span style={{ fontSize: '0.85rem' }}>
                  {loadingSample ? '⏳ ' : '🎲 '}
                  {loadingSample ? t.loading_sample : t.try_random}
                </span>
              </button>
              <button 
                onClick={() => setShowSettings(!showSettings)} 
                className={`control-btn settings-btn ${showSettings ? 'active' : ''}`}
              >
                ⚙️ {t.settings_personalized}
              </button>
              <input 
                ref={fileInputRef}
                type="file" 
                accept="image/*" 
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />
            </div>

            {/* Settings Panel */}
            {showSettings && (
              <div className="settings-panel">
                <h3>⚙️ {t.personal_settings}</h3>
                
                <div className="settings-section">
                  <h4><Shield size={18} /> {t.allergen_alerts}</h4>
                  <div className="tag-grid">
                    {ALLERGEN_OPTIONS.map(allergen => (
                      <button
                        key={allergen}
                        className={`tag-btn ${userAllergens.includes(allergen) ? 'active' : ''}`}
                        onClick={() => toggleAllergen(allergen)}
                      >
                        {(STRINGS[lang]?.allergens?.[allergen]) || allergen}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="settings-section">
                  <h4><Leaf size={18} /> {t.dietary_prefs}</h4>
                  <div className="tag-grid">
                    {DIETARY_PREFERENCES.map(pref => (
                      <button
                        key={pref}
                        className={`tag-btn ${dietaryPreferences.includes(pref) ? 'active' : ''}`}
                        onClick={() => toggleDietaryPref(pref)}
                      >
                        {(STRINGS[lang]?.diet_prefs?.[pref]) || pref}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="settings-section">
                  <h4><TrendingUp size={18} /> {t.health_goal}</h4>
                  <select 
                    value={healthGoal} 
                    onChange={(e) => setHealthGoal(e.target.value)}
                    className="goal-select"
                  >
                    <option value="balanced">{t.goal_balanced}</option>
                    <option value="weight_loss">{t.goal_weight_loss}</option>
                    <option value="muscle_gain">{t.goal_muscle_gain}</option>
                    <option value="heart_healthy">{t.goal_heart_healthy}</option>
                    <option value="keto">Keto</option>
                    <option value="vegan">{t.goal_vegan}</option>
                  </select>
                  <div style={{ marginTop: '0.75rem' }}>
                    <label className="toggle-row">
                      <input
                        type="checkbox"
                        checked={transFatAlert}
                        onChange={(e) => setTransFatAlert(e.target.checked)}
                      />
                      <span>{t.trans_fat_toggle}</span>
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* Webcam */}
            {useWebcam && (
              <div className="webcam-container">
                <Webcam
                  ref={webcamRef}
                  screenshotFormat="image/jpeg"
                  className="webcam"
                  videoConstraints={{ facingMode: facingMode }}
                />
                <div className="webcam-controls">
                  <button 
                    onClick={() => setFacingMode(facingMode === 'user' ? 'environment' : 'user')} 
                    className="flip-camera-btn"
                  >
                    🔄 {t.flip_camera}
                  </button>
                  <button onClick={captureImage} className="capture-btn">
                    📸 {t.capture}
                  </button>
                </div>
              </div>
            )}

            {/* Image Display */}
            {image && !useWebcam && (
              <div className="image-display">
                <img src={image} alt="Food" />
              </div>
            )}

            {!image && !useWebcam && (
              <div className="placeholder">
                <ModeIcon size={64} style={{ color: FOOD_MODES[mode]?.color }} />
                <p>{t.no_image}</p>
                <p className="placeholder-hint">{t.placeholder_hint}</p>
              </div>
            )}
          </div>

          {/* Main Content - Full Width */}
          <div className="main-content">
            {/* Detected Dish Title */}
            {analysis && analysis.analysis && analysis.analysis.food_items && analysis.analysis.food_items.length > 0 && (
              <h2 className="detected-title">
                {analysis.analysis.food_items[0].name}
              </h2>
            )}

            {/* Mode Header and Actions */}
            <div className="mode-content">
              <h2>
                <ModeIcon size={24} style={{ color: FOOD_MODES[mode]?.color }} />
                {(STRINGS[lang]?.modes?.[mode]?.name) || FOOD_MODES[mode]?.name}
              </h2>
              <p className="mode-description">
                {(STRINGS[lang]?.modes?.[mode]?.desc) || FOOD_MODES[mode]?.description}
              </p>
              
              <div className="action-buttons">
                <button 
                  onClick={analyzeFood} 
                  disabled={!image || loading}
                  className="action-btn primary"
                  style={{ '--mode-color': FOOD_MODES[mode]?.color }}
                >
                  {loading ? <Loader className="spin" size={20} /> : <Play size={20} />}
                  {loading ? t.analyzing : t.analyze}
                </button>
              </div>

              {/* Allergen Warnings */}
              {analysis && analysis.allergen_warnings && analysis.allergen_warnings.length > 0 && (
                <div className="allergen-warning">
                  <AlertTriangle size={24} />
                  <div>
                    <strong>{t.allergen_alert_title}</strong>
                    <p>{t.this_food_contains} {analysis.allergen_warnings.join(', ')}</p>
                  </div>
                </div>
              )}

              {/* Trans Fat Warning */}
              {analysis && transFatAlert && analysis.trans_fat_present && (
                <div className="allergen-warning" style={{ borderColor: '#ef4444', background: 'rgba(239,68,68,0.15)' }}>
                  <AlertTriangle size={24} />
                  <div>
                    <strong>{t.trans_fat_alert_title}</strong>
                    <p>{t.trans_fat_alert_text}</p>
                  </div>
                </div>
              )}

              {/* Analysis Results */}
              {analysis && (
                <div className="results">
                  <h3><Flame size={20} /> {t.total_nutrition}</h3>
                  <div className="nutrition-summary">
                    <div className="nutrition-card highlight">
                      <div className="nutrition-value">
                        {analysis.analysis.total_nutrition.calories}
                      </div>
                      <div className="nutrition-label">{t.calories}</div>
                    </div>
                    <div className="nutrition-card">
                      <div className="nutrition-value">
                        {analysis.analysis.total_nutrition.protein}g
                      </div>
                      <div className="nutrition-label">{t.protein}</div>
                    </div>
                    <div className="nutrition-card">
                      <div className="nutrition-value">
                        {analysis.analysis.total_nutrition.carbs}g
                      </div>
                      <div className="nutrition-label">{t.carbs}</div>
                    </div>
                    <div className="nutrition-card">
                      <div className="nutrition-value">
                        {analysis.analysis.total_nutrition.fat}g
                      </div>
                      <div className="nutrition-label">{t.fat}</div>
                    </div>
                  </div>

                  {/* Health Score */}
                  <div className="health-score">
                    <Heart size={20} />
                    <span className="score-label">{t.health_score}:</span>
                    <div className="score-bar">
                      <div 
                        className="score-fill"
                        style={{ 
                          width: `${analysis.analysis.overall_health_score}%`,
                          backgroundColor: analysis.analysis.overall_health_score >= 70 ? '#10b981' : 
                                          analysis.analysis.overall_health_score >= 40 ? '#f59e0b' : '#ef4444'
                        }}
                      />
                    </div>
                    <span className="score-value">{analysis.analysis.overall_health_score}/100</span>
                  </div>

                  <div className="health-summary">
                    {(() => {
                      const s = analysis.analysis.health_summary;
                      const bullets = Array.isArray(s)
                        ? s
                        : (typeof s === 'string' ? s.split('\n').filter(b => b && b.trim()) : []);
                      return bullets.map((bullet, idx) => (
                        <div key={idx}>{bullet}</div>
                      ));
                    })()}
                  </div>

                  {/* Food Items */}
                  <h3>📋 {t.detected_items} ({analysis.analysis.food_items.length})</h3>
                  {analysis.analysis.food_items.map((item, idx) => (
                    <div key={idx} className="food-item">
                      <div className="food-header">
                        <h4>{item.name}</h4>
                        <span className="portion">{item.portion_size}</span>
                      </div>
                      
                      <div className="food-nutrition">
                        <span>🔥 {item.nutrition.calories} cal</span>
                        <span>💪 {item.nutrition.protein}g protein</span>
                        <span>🍞 {item.nutrition.carbs}g carbs</span>
                        <span>🥑 {item.nutrition.fat}g fat</span>
                      </div>

                      {item.allergens && item.allergens.length > 0 && (
                        <div className="food-allergens">
                          <strong>⚠️ {t.allergens_label}</strong> {item.allergens.join(', ')}
                        </div>
                      )}

                      {item.ingredients && item.ingredients.length > 0 && (
                        <div className="food-ingredients">
                          <strong>🥗 {t.ingredients_label}</strong> {item.ingredients.join(', ')}
                        </div>
                      )}

                      {item.has_trans_fat && (
                        <div className="trans-fat-warning">
                          {t.contains_trans_fat}
                        </div>
                      )}

                      <div className="food-health">
                        {item.health_score !== undefined && (
                          <span className="health-score-badge">
                            {item.health_score >= 70 ? '✅' : item.health_score >= 40 ? '⚠️' : '❌'}
                            {item.health_score}/100
                          </span>
                        )}
                        {item.health_notes && (
                          <span className="health-notes">{item.health_notes}</span>
                        )}
                      </div>
                    </div>
                  ))}

                  {/* Dietary Compatibility */}
                  
                  {analysis.analysis.dietary_compatibility && (
                    <div className="compatibility-tags">
                      {Object.entries(analysis.analysis.dietary_compatibility).map(([diet, compatible]) => (
                        compatible && (
                          <span key={diet} className="compatible-tag">
                            ✅ {(STRINGS[lang]?.compat?.[diet]) || diet.replace('_', ' ')}
                          </span>
                        )
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Button to generate recipe shown after analysis and before recipe */}
              {analysis && (
                <div className="action-buttons" style={{ margin: '0.75rem 0' }}>
                  <button
                    onClick={generateRecipe}
                    disabled={loading}
                    className="action-btn secondary"
                  >
                    {loading && <Loader className="spin" size={20} />}
                    {loading ? t.generating : t.generate_personalized_recipe}
                  </button>
                </div>
              )}

              {/* Hidden image loader to trigger onLoad */}
              {recipeImage && !recipe && (
                <img 
                  src={recipeImage} 
                  alt="Loading recipe"
                  style={{ display: 'none' }}
                  onLoad={() => {
                    // Show recipe text only after image loads
                    setTimeout(() => {
                      setRecipe(recipeText);
                      setLoading(false);
                      // Scroll to recipe section after text appears
                      setTimeout(() => {
                        if (recipeRef.current) {
                          recipeRef.current.scrollIntoView({ 
                            behavior: 'smooth', 
                            block: 'start' 
                          });
                        }
                      }, 200);
                    }, 100);
                  }}
                  onError={() => { 
                    // Show text even if image fails
                    setRecipe(recipeText);
                    setLoading(false);
                  }}
                />
              )}

              {/* Recipe - only show when recipe is fully ready */}
                  {recipe && recipeImage && (
                    <div ref={recipeRef} className="results recipe-section">
                      <h3><ChefHat size={20} /> {t.recipe_suggestion}</h3>
                      
                      {/* Recipe Image - visible version */}
                      <div style={{ marginTop: '1rem', marginBottom: '1rem', textAlign: 'center' }}>
                        <img 
                          src={recipeImage} 
                          alt="Recipe visualization"
                          style={{ 
                            width: '100%', 
                            maxWidth: '400px', 
                            height: 'auto',
                            borderRadius: '12px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                          }}
                        />
                      </div>
                      
                      {/* Recipe Text - only show after image loads */}
                      {recipe && (
                        <>
                          <div className="recipe-content">
                            {recipe.split('\n').map((line, idx) => (
                              <p key={idx}>{line}</p>
                            ))}
                          </div>
                          <div className="action-buttons" style={{ marginTop: '1rem' }}>
                            <button
                              onClick={generateRecipe}
                              disabled={loading}
                              className="action-btn secondary"
                            >
                              {loading && <Loader className="spin" size={20} />}
                              {loading ? t.generating : t.generate_another_recipe}
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
            </div>
          </div>
        </div>
      </div>

      <footer className="App-footer">
        <p>{t.footer_line1}</p>
        <p>{t.footer_line2}</p>
      </footer>
    </div>
  );
}

export default App;

